import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ImageUp, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  analyzeImageFile,
  mineQueryKeys,
  type ImageAnalysisResult,
  useCreateImageItemMutation,
} from "@/modules/mine";
import { FolderSelector } from "@/modules/mine/components/folder-selector";
import { TagSelector } from "@/modules/mine/components/tag-selector";
import type { RouteInfo } from "@/modules/mine/navigation/types";
import type { ItemTagData } from "@/modules/mine/types";
import type { DialogComponentProps } from "@/shared/components/dialog";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { DialogFooter } from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";

type AddImageResult = {
  action: "created";
  folderId: string | null;
};

type AnalysisStatus = "idle" | "loading" | "ready" | "error";
type UploadStatus = "idle" | "uploading" | "success" | "error";

interface SelectedImage {
  analysis?: ImageAnalysisResult;
  analysisStatus: AnalysisStatus;
  error?: string;
  file: File;
  id: string;
  previewUrl: string;
  progress: number;
  status: UploadStatus;
}

interface AddImageProps extends DialogComponentProps<AddImageResult> {
  routeInfo: RouteInfo;
}

const MAX_IMAGE_UPLOAD_COUNT = 9;

function getDefaultFolderId(routeInfo: RouteInfo) {
  return routeInfo.folderId ?? null;
}

function createSelectedImage(file: File): SelectedImage {
  return {
    file,
    id: `${file.name}-${file.lastModified}-${crypto.randomUUID()}`,
    previewUrl: URL.createObjectURL(file),
    progress: 0,
    analysisStatus: "idle",
    status: "idle",
  };
}

function getFileTitle(file: File, title: string, index: number, total: number) {
  const normalizedTitle = title.trim();
  if (!normalizedTitle) {
    return file.name.replace(/\.[^.]+$/, "");
  }

  if (total === 1) {
    return normalizedTitle;
  }

  return `${normalizedTitle} ${index + 1}`;
}

function getImageStatusText(image: SelectedImage) {
  if (image.status === "success") {
    return "上传完成";
  }

  if (image.status === "error") {
    return image.error ?? "上传失败";
  }

  if (image.status === "uploading") {
    return "上传中";
  }

  return "等待上传";
}

function getImageMetaText(image: SelectedImage) {
  if (image.analysisStatus === "loading") {
    return "分析中";
  }

  if (image.analysis) {
    return `${image.analysis.width} × ${image.analysis.height}`;
  }

  return null;
}

function getImageVisualSwatches(image: SelectedImage) {
  return image.analysis?.visual.palettes.slice(0, 3) ?? [];
}

export function AddImage({ close, resolve, routeInfo, setSubmitting }: AddImageProps) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    getDefaultFolderId(routeInfo),
  );
  const [tags, setTags] = useState<ItemTagData[]>([]);
  const [images, setImages] = useState<SelectedImage[]>([]);
  const imagesRef = useRef<SelectedImage[]>([]);
  const dragDepthRef = useRef(0);
  const queryClient = useQueryClient();
  const createImageItemMutation = useCreateImageItemMutation();

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(() => {
    return () => {
      imagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    };
  }, []);

  const updateImage = (id: string, nextImage: Partial<SelectedImage>) => {
    setImages((currentImages) =>
      currentImages.map((image) =>
        image.id === id ? { ...image, ...nextImage } : image,
      ),
    );
  };

  const analyzeSelectedImage = async (image: SelectedImage) => {
    updateImage(image.id, { analysisStatus: "loading" });

    try {
      const analysis = await analyzeImageFile(image.file);
      updateImage(image.id, {
        analysis,
        analysisStatus: "ready",
      });
    } catch {
      updateImage(image.id, { analysisStatus: "error" });
    }
  };

  const handleFilesChange = (files: FileList | null) => {
    const nextFiles = Array.from(files ?? []).filter((nextFile) =>
      nextFile.type.startsWith("image/"),
    );

    if (nextFiles.length === 0) {
      return;
    }

    const remainingCount = MAX_IMAGE_UPLOAD_COUNT - images.length;
    if (remainingCount <= 0) {
      toast.warning(`每次最多上传 ${MAX_IMAGE_UPLOAD_COUNT} 张图片`);
      return;
    }

    const acceptedFiles = nextFiles.slice(0, remainingCount);

    if (acceptedFiles.length < nextFiles.length) {
      toast.warning(`每次最多上传 ${MAX_IMAGE_UPLOAD_COUNT} 张图片`);
    }

    if (!title.trim() && images.length === 0 && acceptedFiles.length === 1) {
      setTitle(acceptedFiles[0].name.replace(/\.[^.]+$/, ""));
    }

    const nextImages = acceptedFiles.map(createSelectedImage);

    setImages((currentImages) => [...currentImages, ...nextImages]);
    nextImages.forEach((image) => {
      void analyzeSelectedImage(image);
    });
  };

  const handleRemoveImage = (id: string) => {
    setImages((currentImages) => {
      const removedImage = currentImages.find((image) => image.id === id);
      if (removedImage) {
        URL.revokeObjectURL(removedImage.previewUrl);
      }

      return currentImages.filter((image) => image.id !== id);
    });
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragActive(false);

    if (isUploading) {
      return;
    }

    handleFilesChange(event.dataTransfer.files);
  };

  const handleDragEnter = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    if (isUploading) {
      return;
    }

    dragDepthRef.current += 1;
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

    if (dragDepthRef.current === 0) {
      setIsDragActive(false);
    }
  };

  const uploadSingleImage = async (
    image: SelectedImage,
    index: number,
    total: number,
  ) => {
    updateImage(image.id, {
      error: undefined,
      progress: 0,
      status: "uploading",
    });

    try {
      await createImageItemMutation.mutateAsync({
        file: image.file,
        folder_id: selectedFolderId ?? undefined,
        title: getFileTitle(image.file, title, index, total),
        note: note.trim() || undefined,
        tags: {
          tag_ids: tags.map((tag) => tag.id),
        },
        analysis: image.analysis,
        onUploadProgress: (progress) => {
          updateImage(image.id, { progress });
        },
      });

      updateImage(image.id, {
        progress: 100,
        status: "success",
      });

      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : "图片上传失败";
      updateImage(image.id, {
        error: message,
        status: "error",
      });
      return false;
    }
  };

  const handleRetryImage = async (
    event: React.MouseEvent<HTMLButtonElement>,
    image: SelectedImage,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    setSubmitting(true);
    let success = false;

    try {
      success = await uploadSingleImage(image, 0, 1);
      if (success) {
        await queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
      }
    } finally {
      setSubmitting(false);
    }

    if (success) {
      toast.success(`${image.file.name} 上传成功`);
      return;
    }

    toast.error(`${image.file.name} 上传失败`);
  };

  const handleSubmit = async () => {
    if (images.length === 0) {
      toast.error("请选择图片文件");
      return;
    }

    const uploadTargets = images.filter((image) => image.status !== "success");
    if (uploadTargets.length === 0) {
      resolve({
        action: "created",
        folderId: selectedFolderId,
      });
      return;
    }

    setSubmitting(true);
    let successCount = 0;

    try {
      const uploadResults = await Promise.all(
        uploadTargets.map((image, index) =>
          uploadSingleImage(image, index, uploadTargets.length),
        ),
      );
      successCount = uploadResults.filter(Boolean).length;

      if (successCount > 0) {
        await queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });
      }
    } finally {
      setSubmitting(false);
    }

    if (successCount === uploadTargets.length) {
      toast.success(`已上传 ${successCount} 张图片`);
      resolve({
        action: "created",
        folderId: selectedFolderId,
      });
      return;
    }

    if (successCount > 0) {
      toast.warning(`已上传 ${successCount} 张图片，部分图片上传失败`);
      return;
    }

    toast.error("图片上传失败");
  };

  const isUploading = images.some((image) => image.status === "uploading");

  return (
    <div className="space-y-5">
      <div className="grid items-stretch gap-5 lg:grid-cols-[1.4fr_1fr]">
        <div className="flex min-h-[360px] flex-col">
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="image-file" className="text-xs text-muted-foreground">
              选择图片
            </Label>
            <input
              id="image-file"
              type="file"
              accept="image/*"
              multiple
              maxLength={MAX_IMAGE_UPLOAD_COUNT}
              max={MAX_IMAGE_UPLOAD_COUNT}
              disabled={isUploading}
              className="sr-only"
              onChange={(event) => {
                handleFilesChange(event.target.files);
                event.target.value = "";
              }}
            />
            <Label
              htmlFor="image-file"
              className={cn(
                "block flex-1 cursor-pointer overflow-hidden rounded-xl border border-dashed border-border bg-muted/20 transition-colors hover:border-foreground/30 hover:bg-muted/40 data-[disabled=true]:pointer-events-none data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-60",
                isDragActive &&
                  "border-primary bg-primary/5 shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.18)]",
              )}
              data-disabled={isUploading}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleDrop}
            >
              {images.length > 0 ? (
                <span className="flex h-full flex-col">
                  <span className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
                    {images.map((image) => (
                      <span
                        key={image.id}
                        className="group relative flex items-center gap-2.5 rounded-lg border bg-background px-2.5 py-2"
                        onClick={(event) => {
                          event.preventDefault();
                        }}
                      >
                        <span className="relative shrink-0 overflow-hidden rounded-md border bg-muted">
                          <img
                            src={image.previewUrl}
                            alt={image.file.name}
                            className="size-12 object-cover"
                          />
                        </span>
                        <button
                          type="button"
                          className="absolute top-1 right-1 inline-flex size-6 items-center justify-center rounded-full bg-background/90 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100 disabled:pointer-events-none"
                          disabled={image.status === "uploading"}
                          onClick={() => {
                            handleRemoveImage(image.id);
                          }}
                        >
                          <X className="size-3.5" />
                        </button>

                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="truncate text-xs font-medium">
                            {image.file.name}
                          </span>
                          <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                            {getImageMetaText(image) ? (
                              <span>{getImageMetaText(image)}</span>
                            ) : null}
                            {getImageVisualSwatches(image).length ? (
                              <span className="flex items-center gap-1">
                                {getImageVisualSwatches(image).map((color) => (
                                  <span
                                    key={`${image.id}-${color.hex}`}
                                    className="size-2 rounded-full border border-black/5"
                                    style={{ backgroundColor: color.hex }}
                                    title={`${color.hex} · ${(color.coverage * 100).toFixed(0)}%`}
                                  />
                                ))}
                              </span>
                            ) : null}
                          </span>
                          <span className="flex items-center justify-between text-[11px] text-muted-foreground">
                            <span className="truncate pr-3">
                              {getImageStatusText(image)}
                            </span>
                            {image.status === "error" ? (
                              <button
                                type="button"
                                className="shrink-0 text-emerald-600 transition-colors hover:text-emerald-700"
                                disabled={isUploading}
                                onClick={(event) => void handleRetryImage(event, image)}
                              >
                                重传
                              </button>
                            ) : image.status === "success" ? (
                              <CheckCircle2 className="size-3.5 shrink-0 text-green-600" />
                            ) : (
                              <span className="shrink-0">{image.progress}%</span>
                            )}
                          </span>
                          <span className="block h-1.5 overflow-hidden rounded-full bg-muted">
                            <span
                              className="block h-full rounded-full bg-emerald-400/80 transition-all"
                              style={{ width: `${image.progress}%` }}
                            />
                          </span>
                        </span>
                      </span>
                    ))}
                  </span>
                  <span
                    className={cn(
                      "flex items-center justify-center gap-2 border-t bg-muted/30 px-4 py-3 text-xs text-muted-foreground transition-colors",
                      isDragActive && "border-primary/20 bg-primary/5 text-primary",
                    )}
                  >
                    <ImageUp className="size-4" />
                    点击或拖拽继续添加图片
                  </span>
                </span>
              ) : (
                <span className="flex h-full min-h-[330px] flex-col items-center justify-center gap-3 px-4 py-6 text-center">
                  <span
                    className={cn(
                      "inline-flex size-11 items-center justify-center rounded-full bg-background text-muted-foreground shadow-sm ring-1 ring-border transition-colors",
                      isDragActive &&
                        "bg-primary/8 text-primary ring-primary/30",
                    )}
                  >
                    <ImageUp className="size-5" />
                  </span>
                  <span className="space-y-1">
                    <span
                      className={cn(
                        "block text-sm font-medium text-muted-foreground transition-colors",
                        isDragActive && "text-primary",
                      )}
                    >
                      点击选择图片，或拖拽图片到这里
                    </span>
                    <span
                      className={cn(
                        "block text-xs text-muted-foreground transition-colors",
                        isDragActive && "text-primary/80",
                      )}
                    >
                      支持多选，每次最多上传 {MAX_IMAGE_UPLOAD_COUNT} 张图片
                    </span>
                  </span>
                </span>
              )}
            </Label>
          </div>
        </div>

        <div className="flex min-h-[360px] flex-col gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="image-title" className="text-xs text-muted-foreground">
              标题
            </Label>
            <Input
              id="image-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={images.length > 1 ? "可选标题前缀" : "可选标题"}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">文件夹</Label>
            <FolderSelector
              value={selectedFolderId}
              onChange={setSelectedFolderId}
              placeholder="未分类"
              allowEmpty={true}
              emptyLabel="未分类"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">标签</Label>
            <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1">
              <TagSelector
                value={tags}
                onChange={setTags}
                triggerClassName="bg-muted px-2 hover:bg-muted/80 [&_svg]:size-3"
              />
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="image-note" className="text-xs text-muted-foreground">
              备注
            </Label>
            <Textarea
              id="image-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="可选备注"
              className="min-h-0 flex-1 resize-none [field-sizing:fixed]"
            />
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={close}
          disabled={isUploading}
        >
          取消
        </Button>
        <Button type="button" onClick={handleSubmit} disabled={isUploading}>
          {isUploading ? <Loader2 className="animate-spin" /> : null}
          确认
        </Button>
      </DialogFooter>
    </div>
  );
}
